import type { WooStoreConfig } from "../types";
import { resolveFluxmedAccountLinkSuccessUrl } from "../constants/accountLinkSuccessRoute";
import {
  resolveWordPressSiteBase,
  WordPressSiteOriginMissingError,
} from "./wordpressSiteBase";

const POPUP_FEATURES = "width=600,height=800,scrollbars=yes";

/**
 * Monta URL de SSO WordPress via wp-login.php com force_redirect=1.
 * O plugin OpenID Connect Generic redireciona imediatamente ao IdP Fluxmed.
 */
export function buildWordPressSsoRedirectUrl(
  store: WooStoreConfig,
  redirectTo: string,
): string {
  let base: string;
  try {
    base = resolveWordPressSiteBase(store.siteOrigin);
  } catch {
    throw new WordPressSiteOriginMissingError(store.name);
  }
  const params = new URLSearchParams({
    redirect_to: redirectTo,
    force_redirect: "1",
  });
  return `${base}/wp-login.php?${params.toString()}`;
}

/** SSO wp-login → página de sucesso no domínio Fluxmed (vinculação de conta). */
export function buildCartLoginRedirectUrl(store: WooStoreConfig): string {
  const redirectTo = resolveFluxmedAccountLinkSuccessUrl(store.name);
  return buildWordPressSsoRedirectUrl(store, redirectTo);
}

/** SSO wp-login → página de pagamento do pedido (order-pay). */
export function buildPaymentLoginRedirectUrl(
  store: WooStoreConfig,
  paymentUrl: string,
): string {
  return buildWordPressSsoRedirectUrl(store, paymentUrl);
}

function openWordPressSsoPopupWindow(
  loginRedirectUrl: string,
  windowName: string,
): Window | null {
  return window.open(loginRedirectUrl, windowName, POPUP_FEATURES);
}

function openWordPressSsoPopupWithFallback(
  loginRedirectUrl: string,
  windowName: string,
): Window | null {
  const popup = window.open(loginRedirectUrl, windowName, POPUP_FEATURES);

  if (!popup) {
    window.location.href = loginRedirectUrl;
  }

  return popup;
}

function monitorPopupClose(popup: Window): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        resolve();
      }
    }, 500);
  });
}

/**
 * Abre popup de login SSO WordPress (wp-login → IdP → página de sucesso Fluxmed).
 * Retorna referência da janela (null se popup bloqueado — fallback redireciona a aba atual).
 */
export function openFluxmedOpenIdLoginWindow(
  store: WooStoreConfig,
): Window | null {
  const loginRedirectUrl = buildCartLoginRedirectUrl(store);
  return openWordPressSsoPopupWithFallback(
    loginRedirectUrl,
    "FluxmedOpenIdLogin",
  );
}

/**
 * Abre popup de login SSO e resolve quando o popup é fechado.
 * Fallback: se popup bloqueado, redireciona a janela atual.
 */
export function openFluxmedOpenIdLogin(store: WooStoreConfig): Promise<void> {
  const popup = openFluxmedOpenIdLoginWindow(store);

  if (!popup) {
    return Promise.resolve();
  }

  return monitorPopupClose(popup);
}

export function openPaymentPopup(
  store: WooStoreConfig,
  paymentUrl: string,
): void {
  const loginRedirectUrl = buildPaymentLoginRedirectUrl(store, paymentUrl);
  openWordPressSsoPopupWithFallback(
    loginRedirectUrl,
    `Pagamento-${store.id}`,
  );
}

/** Abre popup de pagamento (SSO → order-pay) e retorna referência da janela. */
export function openPaymentPopupWindow(
  store: WooStoreConfig,
  paymentUrl: string,
): Window | null {
  const loginRedirectUrl = buildPaymentLoginRedirectUrl(store, paymentUrl);
  return openWordPressSsoPopupWindow(
    loginRedirectUrl,
    `Pagamento-${store.id}-${Date.now()}`,
  );
}

/** Abre popup de pagamento e resolve quando o usuário fechar a janela. */
export function openPaymentPopupMonitored(
  store: WooStoreConfig,
  paymentUrl: string,
): Promise<{ closed: true }> {
  return new Promise((resolve) => {
    const popup = openPaymentPopupWindow(store, paymentUrl);

    if (!popup) {
      openPaymentPopup(store, paymentUrl);
      resolve({ closed: true });
      return;
    }

    void monitorPopupClose(popup).then(() => {
      resolve({ closed: true });
    });
  });
}
